import { SORT_ORDERS } from '@/libs/constants'
import environment from '@/environments/environment.dev'

import HttpClient from '../apiHttpClient'

const SUPPORT_GET_USERS_LIST = '/v1.0/support/users/list'
const SUPPORT_ADD_TO_CART = '/v1.0/support/item/add'
const SUPPORT_EMPTY_CART = '/v1.0/support/items/empty'
const SUPPORT_GET_MACHINES_LIST = '/v1.0/support/maschine/list'
const SUPPORT_GET_ORDER_DETAILS = '/v1.0/support/order/{orderNumber}/detail'
const SUPPORT_GET_ORDERS_LIST = '/v1.0/support/order/list'
const SUPPORT_GET_CART = '/v1.0/support/simulate'
const SUPPORT_SYNC_ORDERS = '/v1.0/support/ordersync'
const SUPPORT_CLEAR_CACHE = '/v1.0/utility/deletecountrykeys'
const SUPPORT_GET_WISHLIST_LIST = '/v1.0/support/wishlist/overview'
const SUPPORT_GET_WISHLIST_DETAILS = '/v1.0/support/wishlist/detail'
const SUPPORT_ADD_NEW_WISHLIST = '/v1.0/support/wishlist/create'
const SUPPORT_ADD_PRODUCT_TO_WISHLIST = '/v1.0/support/wishlist/additem'
const SUPPORT_UPDATE_WISHLIST_ITEM = '/v1.0/support/wishlist/updateitem'
const SUPPORT_DELETE_WISHLIST = '/v1.0/support/wishlist/delete'
const SUPPORT_REMOVE_WISHLIST_PRODUCT = '/v1.0/support/wishlist/removeitem'
const SUPPORT_ADD_WISHLIST_TO_CART = '/v1.0/support/wishlist/add-to-cart'
const SUPPORT_RENAME_WISHLIST = '/v1.0/support/wishlist/rename'

const fapiClient = new HttpClient(environment.adminFAPIUrl)

interface UsersListParameters {
  current: number
  pageSize: number
  sortBy: string
  orderBy: string
  searchTerm?: string
  selectedLetter?: string
  country: string
}

interface AddToCartParameters {
  materialNumber: string
  quantity: number
  countryId: string
  userId: string
}

interface DeleteCartParameters {
  userId: string
}

interface MachinesListParameters {
  userId: string
  pageNumber: number
  pageSize: number
  sortOrder: string | number
}

interface OrderDetailsParameters {
  userId: string
  countryId: string
  orderNumber: string
}

interface OrdersListParameters {
  customerId: string
  countryId: string
  pageNumber?: number
  pageSize?: number
  orderBy?: string
  sortBy?: string
}

type QueryParameters = Record<string, string | number | undefined>

/**
 * Helper to filter out undefined params (ensures query params are clean)
 */
const cleanQueryParameters = (parameters: QueryParameters) => {
  return Object.fromEntries(
    Object.entries(parameters).filter(([_, value]) => value !== undefined),
  )
}

/**
 * API Functions
 */

// Get users list
const getUsersList = async (payload: UsersListParameters) => {
  const queryParameters = cleanQueryParameters({
    pageNumber: payload.current,
    pageSize: payload.pageSize,
    sortBy: payload.sortBy,
    orderBy: payload.orderBy,
    searchTerm: payload.searchTerm,
    selectedLetter: payload.selectedLetter,
    countryId: payload.country,
  })

  return fapiClient.get(SUPPORT_GET_USERS_LIST, {
    params: queryParameters,
  })
}

// Add product to customer cart
const addProductToCustomerCart = async ({
  materialNumber,
  quantity,
  countryId,
  userId,
}: AddToCartParameters) =>
  fapiClient.post(SUPPORT_ADD_TO_CART, {
    body: { materialNumber, quantity, countryId, userId },
  })

// Delete cart for given user ID
const deleteCartForGivenUserId = async ({ userId }: DeleteCartParameters) =>
  fapiClient.delete(SUPPORT_EMPTY_CART, { body: { userId } })

// Get machines list by customer number
const getMachinesListByCustomerNumber = async ({
  userId,
  pageNumber,
  pageSize,
  sortOrder,
}: MachinesListParameters) =>
  fapiClient.get(SUPPORT_GET_MACHINES_LIST, {
    params: { userId, pageNumber, pageSize, sortOrder },
  })

// Get order details for user
const getOrderByUser = async ({
  userId,
  countryId,
  orderNumber,
}: OrderDetailsParameters) =>
  fapiClient.get(
    SUPPORT_GET_ORDER_DETAILS.replace('{orderNumber}', orderNumber),
    {
      params: { userId, countryId },
    },
  )

// Get all orders by customer (with default pagination)
const getAllOrdersByCustomer = async ({
  customerId,
  countryId,
  pageNumber = 1,
  pageSize = 10,
  orderBy = SORT_ORDERS.descend,
  sortBy,
}: OrdersListParameters) =>
  fapiClient.get(SUPPORT_GET_ORDERS_LIST, {
    params: { customerId, countryId, pageNumber, pageSize, orderBy, sortBy },
  })

// Get cart data by user ID
const getCartDataByUserId = async (userId: string) =>
  fapiClient.get(SUPPORT_GET_CART, { params: { userId } })

// Synchronize orders by user ID
const synchronizeOrdersByUserId = async (userId: string) =>
  fapiClient.post(SUPPORT_SYNC_ORDERS, {
    params: { userId },
    body: {},
  })

// Clear cache for country
const clearCacheForCountry = async (countryId: string) =>
  fapiClient.delete(SUPPORT_CLEAR_CACHE, {
    params: { countryId },
  })

const getWishlistList = async ({
  ownerId,
  countryId,
  pageNumber = 1,
  pageSize = 10,
  searchQuery,
}) => {
  return fapiClient.get(SUPPORT_GET_WISHLIST_LIST, {
    params: {
      ownerId,
      pageNumber,
      pageSize,
      searchQuery,
      countryId,
    },
  })
}

const getWishlistDetails = async ({
  ownerId,
  wishlistId,
  pageNumber = 1,
  pageSize = 10,
  searchQuery,
}) => {
  return fapiClient.get(SUPPORT_GET_WISHLIST_DETAILS, {
    params: {
      ownerId,
      wishlistId,
      pageNumber,
      pageSize,
      searchQuery,
    },
  })
}

const addNewWishlist = async ({ ownerId, wishlistName }) => {
  return fapiClient.post(SUPPORT_ADD_NEW_WISHLIST, {
    body: {},
    params: { ownerId, wishlistName },
  })
}

const addProductToWishlist = async ({
  ownerId,
  wishlistId,
  materialNumber,
  quantity = 1,
}) => {
  return fapiClient.put(SUPPORT_ADD_PRODUCT_TO_WISHLIST, {
    body: {},
    params: { ownerId, wishlistId, materialNumber, quantity },
  })
}

const addWishlistToCart = async ({ ownerId, wishlistId, sessionId }) => {
  return fapiClient.post(SUPPORT_ADD_WISHLIST_TO_CART, {
    body: {},
    params: { ownerId, wishlistId, sessionId },
  })
}

const updateProductQuantity = async ({
  ownerId,
  wishlistId,
  materialNumber,
  quantity = 1,
}) => {
  return fapiClient.put(SUPPORT_UPDATE_WISHLIST_ITEM, {
    body: {},
    params: { ownerId, wishlistId, materialNumber, quantity },
  })
}

const deleteWishlist = async ({ ownerId, wishlistId }) => {
  return fapiClient.delete(SUPPORT_DELETE_WISHLIST, {
    body: {},
    params: { ownerId, wishlistId },
  })
}

const removeWishlistProduct = async ({ ownerId, wishlistId, materialNumber }) => {
  return fapiClient.delete(SUPPORT_REMOVE_WISHLIST_PRODUCT, {
    body: {},
    params: { ownerId, wishlistId, materialNumber },
  })
}

const renameWishlist = async ({ ownerId, wishlistId, newName }) => {
  return fapiClient.post(SUPPORT_RENAME_WISHLIST, {
    body: {},
    params: { ownerId, wishlistId, newName },
  })
}

export const adminFapiSupport = {
  getUsersList,
  addProductToCustomerCart,
  deleteCartForGivenUserId,
  getMachinesListByCustomerNumber,
  getOrderByUser,
  getAllOrdersByCustomer,
  getCartDataByUserId,
  synchronizeOrdersByUserId,
  clearCacheForCountry,
  getWishlistList,
  getWishlistDetails,
  addNewWishlist,
  addProductToWishlist,
  updateProductQuantity,
  deleteWishlist,
  removeWishlistProduct,
  addWishlistToCart,
  renameWishlist,
}
